import type { Collection, Model } from '@/newApi/services/ResgateManager';
import { MathsServiceError } from '@sysarb/sysarb-types/services/v1/maths-service';
import type {
    AdjustedSalaryFactor,
    CategoricalFactor,
    CategoricalFactorChild,
    CreateAdjustedSalaryFactorInput,
    NumericalFactor,
    PayEquityAuditMathsSummary,
} from '@sysarb/sysarb-types-v2/services/v1/pay-equity-audit';
import { FactorName, SignificanceTrend } from '@sysarb/sysarb-types-v2/services/v1/pay-equity-audit';
import { FactorTypes } from '@sysarb/sysarb-types-v2/services/v1/pay-equity-audit/enums';
import type { Ref } from 'vue';
import { ref } from 'vue';

// ─── Scenario Types ───────────────────────────────────────────────────────────

/**
 * Switch between mock scenarios to test different states of the model insights page.
 * Change `currentMockScenario.value` at runtime to switch scenarios.
 */
export type MockScenario =
    // Pay gap direction scenarios
    | 'men-favoured-good-model'     // Default: men earn more unadjusted and adjusted, good model
    | 'women-favoured'              // Women earn more on both unadjusted and adjusted
    | 'no-significant-gap'          // adjustedPayGap near zero, no significant difference
    | 'crossover'                   // Unadjusted men, adjusted women (gap reversed by factors)
    // Model strength scenarios
    | 'weak-model'                  // combinedModelStrength ~0.12, modelStrengthLevel: 'weak'
    | 'moderate-model'              // combinedModelStrength ~0.38, modelStrengthLevel: 'moderate'
    | 'strong-model'                // combinedModelStrength ~0.85, modelStrengthLevel: 'strong'
    | 'asymmetric-r2'               // Men ~0.72 (good), Women ~0.22 (weak) → combinedModelStrength: 0.22
    // Data quality scenario
    | 'needs-more-data'             // AGE factor excluded due to insufficient data (INSUFFICIENT_VALUES)
    // Hard error scenarios — queryFn throws, composable shows error alert
    | 'error-e001'                  // E001: All factors aliased/perfectly correlated
    | 'error-e002'                  // E002: Model not statistically significant
    | 'error-e003'                  // E003: Pay gap already within accepted range
    | 'error-e004'                  // E004: No ID column provided in request body
    | 'error-e005'                  // E005: No ID column found in employments
    | 'error-e006'                  // E006: No strategy provided
    | 'error-e007'                  // E007: Quantile regression model failed to converge
    | 'error-e008'                  // E008: Perfect fit — n=30 employees + 2 factors → overfitting
    | 'error-e009'                  // E009: R² value missing from model output
    | 'error-e010'                  // E010: Only one gender group — all 50 employees are male
    | 'error-e011'                  // E011: One or more predictors are constant
    | 'error-e012'                  // E012: Data type error in factor values
    | 'error-e013'                  // E013: OLS regression failed
    | 'error-default'               // Default fallback — unknown / unmapped error code
    // Soft error scenarios — return data with limitations
    | 'soft-error-e001s'            // E001S: AGE aliases with EMPLOYMENT_TIME (r≈0.999), model still runs
    | 'soft-error-e014';            // E014: Pay gap ~300 SEK (~0.07%) — too small for decomposition

/** Switch the active mock scenario. Reactive — changing this triggers query re-evaluation. */
export const currentMockScenario = ref<MockScenario>('men-favoured-good-model');

// ─── Composable ───────────────────────────────────────────────────────────────

export function useMockSalaryFactors(): {
    salaryFactors: Ref<Collection<AdjustedSalaryFactor>>;
    addMockSalaryFactor: (input: CreateAdjustedSalaryFactorInput) => void;
    removeMockSalaryFactor: (id: string) => void;
    generateMockSalaryFactorAnalysis: (factors: AdjustedSalaryFactor[]) => PayEquityAuditMathsSummary & { explainedPayGap: number };
} {
    function mockModel<T>(data: T): Model<T> {
        return data as unknown as Model<T>;
    }

    const salaryFactors: Ref<Collection<AdjustedSalaryFactor>> = ref({ list: [] }) as unknown as Ref<Collection<AdjustedSalaryFactor>>;

    // ─── Numerical Factor Registry ────────────────────────────────────────────
    //
    // Based on DS-01 test dataset: 100 employees, n=50M/50F, salaries in SEK/year.
    // decomposedPayGapPercentage fields are in decimal form: 0.07 = 7%, etc.
    //
    //   VALUATION_POINTS → both groups significant, men have higher mean (increases gap)
    //   AGE              → neither group significant (aliases with EMPLOYMENT_TIME in E001S)
    //   EMPLOYMENT_TIME  → men only significant; men avg 15.5 yr tenure vs women 10.2 yr
    //   PERFORMANCE      → women only significant, women score higher on rating (reduces gap)

    const numericalFactorRegistry: Partial<Record<string, NumericalFactor>> = {
        [FactorName.VALUATION_POINTS]: {
            id: FactorName.VALUATION_POINTS,
            type: FactorTypes.Linear,
            decomposedPayGapPercentage: 0.07,
            malePercentageEffectOnSalary: 0.07,
            femalePercentageEffectOnSalary: 0.06,
            coefficient: 0.002,
            coefficientMale: 0.002,
            coefficientFemale: 0.0018,
            pValueMen: 0.001,
            pValueWomen: 0.0008,
            isSignificant: true,
            isSignificantMen: true,
            isSignificantWomen: true,
            excluded: false,
            excludedReason: null,
            significanceTrend: SignificanceTrend.BOTH,
            meanValueMen: 120,
            meanValueWomen: 108,
            canBeImported: false,
        },
        [FactorName.AGE]: {
            // Neither group significant — in E001S this factor is excluded (ALIASED)
            // because hire date = DOB + exactly 22 years in test data
            id: FactorName.AGE,
            type: FactorTypes.Linear,
            decomposedPayGapPercentage: 0.02,
            malePercentageEffectOnSalary: 0.02,
            femalePercentageEffectOnSalary: 0.01,
            coefficient: 0.001,
            coefficientMale: 0.001,
            coefficientFemale: 0.0008,
            pValueMen: 0.62,
            pValueWomen: 0.66,
            isSignificant: false,
            isSignificantMen: false,
            isSignificantWomen: false,
            excluded: false,
            excludedReason: null,
            significanceTrend: SignificanceTrend.NEITHER,
            meanValueMen: 42,
            meanValueWomen: 40,
            canBeImported: true,
        },
        [FactorName.EMPLOYMENT_TIME]: {
            // Men significantly more tenured (avg 15.5 yr vs 10.2 yr) — men-group only significant
            id: FactorName.EMPLOYMENT_TIME,
            type: FactorTypes.Linear,
            decomposedPayGapPercentage: 0.05,
            malePercentageEffectOnSalary: 0.05,
            femalePercentageEffectOnSalary: 0.02,
            coefficient: 0.003,
            coefficientMale: 0.004,
            coefficientFemale: 0.002,
            pValueMen: 0.008,
            pValueWomen: 0.51,
            isSignificant: true,
            isSignificantMen: true,
            isSignificantWomen: false,
            excluded: false,
            excludedReason: null,
            significanceTrend: SignificanceTrend.MEN,
            meanValueMen: 15.5,
            meanValueWomen: 10.2,
            canBeImported: true,
        },
        [FactorName.PERFORMANCE]: {
            // Women score higher on performance rating (3.8 vs 3.5) — women-group only significant
            id: FactorName.PERFORMANCE,
            type: FactorTypes.Linear,
            decomposedPayGapPercentage: -0.02,
            malePercentageEffectOnSalary: -0.01,
            femalePercentageEffectOnSalary: -0.03,
            coefficient: -0.002,
            coefficientMale: -0.001,
            coefficientFemale: -0.003,
            pValueMen: 0.74,
            pValueWomen: 0.012,
            isSignificant: true,
            isSignificantMen: false,
            isSignificantWomen: true,
            excluded: false,
            excludedReason: null,
            significanceTrend: SignificanceTrend.WOMEN,
            meanValueMen: 3.5,
            meanValueWomen: 3.8,
            canBeImported: true,
        },
        [FactorName.POSITION_LEVEL]: {
            // fpiV1 treats positionlevel as linear (grade/level score). Men avg 3.2, women avg 2.8.
            id: FactorName.POSITION_LEVEL,
            type: FactorTypes.Linear,
            decomposedPayGapPercentage: 0.04,
            malePercentageEffectOnSalary: 0.04,
            femalePercentageEffectOnSalary: 0.035,
            coefficient: 0.004,
            coefficientMale: 0.004,
            coefficientFemale: 0.0035,
            pValueMen: 0.002,
            pValueWomen: 0.001,
            isSignificant: true,
            isSignificantMen: true,
            isSignificantWomen: true,
            excluded: false,
            excludedReason: null,
            significanceTrend: SignificanceTrend.BOTH,
            meanValueMen: 3.2,
            meanValueWomen: 2.8,
            canBeImported: true,
        },
        [FactorName.TIME_IN_CURRENT_POSITION]: {
            // Men have been in current position longer on average (4.2 yr vs 3.1 yr) — both significant
            id: FactorName.TIME_IN_CURRENT_POSITION,
            type: FactorTypes.Linear,
            decomposedPayGapPercentage: 0.03,
            malePercentageEffectOnSalary: 0.03,
            femalePercentageEffectOnSalary: 0.02,
            coefficient: 0.0025,
            coefficientMale: 0.003,
            coefficientFemale: 0.002,
            pValueMen: 0.003,
            pValueWomen: 0.026,
            isSignificant: true,
            isSignificantMen: true,
            isSignificantWomen: true,
            excluded: false,
            excludedReason: null,
            significanceTrend: SignificanceTrend.BOTH,
            meanValueMen: 4.2,
            meanValueWomen: 3.1,
            canBeImported: true,
        },
    };

    // ─── Categorical Factor Registry ──────────────────────────────────────────
    //
    // Based on DS-01/DS-10 test datasets (n=50M/50F). Covers all exclusion/significance patterns.
    // decomposedPayGapPercentage fields are in decimal form: 0.30 = 30%, 0.05 = 5%, etc.

    const categoricalFactorRegistry: Partial<Record<string, CategoricalFactor>> = {
        [FactorName.POSITION_LEVEL]: {
            id: FactorName.POSITION_LEVEL,
            type: FactorTypes.Categorical,
            decomposedPayGapPercentage: 0.05,
            malePercentageEffectOnSalary: null,
            femalePercentageEffectOnSalary: null,
            coefficient: 0,
            coefficientMale: 0,
            coefficientFemale: 0,
            pValueMen: null,
            pValueWomen: null,
            isSignificant: true,
            isSignificantMen: true,
            isSignificantWomen: true,
            excluded: false,
            excludedReason: null,
            significanceTrend: SignificanceTrend.BOTH,
            canBeImported: true,
            countSingleGendered: 1,
            countAliased: 0,
            children: [
                {
                    parentId: FactorName.POSITION_LEVEL,
                    displayValue: 'Junior',
                    representativeShareAllMen: 0.20,
                    representativeShareAllWomen: 0.32,
                    type: FactorTypes.CategoricalChild,
                    decomposedPayGapPercentage: 0,
                    malePercentageEffectOnSalary: null,
                    femalePercentageEffectOnSalary: null,
                    coefficient: 0,
                    coefficientMale: 0,
                    coefficientFemale: 0,
                    pValueMen: null,
                    pValueWomen: null,
                    isSignificant: false,
                    isSignificantMen: null,
                    isSignificantWomen: null,
                    excluded: true,
                    excludedReason: 'REFERENCE_CATEGORY',
                    significanceTrend: SignificanceTrend.NEITHER,
                } satisfies CategoricalFactorChild,
                {
                    // Both groups significant — women over-represented at Mid level, reduces gap
                    parentId: FactorName.POSITION_LEVEL,
                    displayValue: 'Mid',
                    representativeShareAllMen: 0.36,
                    representativeShareAllWomen: 0.40,
                    type: FactorTypes.CategoricalChild,
                    decomposedPayGapPercentage: -0.02,
                    malePercentageEffectOnSalary: 0.12,
                    femalePercentageEffectOnSalary: 0.10,
                    coefficient: 0.11,
                    coefficientMale: 0.12,
                    coefficientFemale: 0.10,
                    pValueMen: 0.003,
                    pValueWomen: 0.004,
                    isSignificant: true,
                    isSignificantMen: true,
                    isSignificantWomen: true,
                    excluded: false,
                    excludedReason: null,
                    significanceTrend: SignificanceTrend.BOTH,
                } satisfies CategoricalFactorChild,
                {
                    // Men only significant — men over-represented at Senior level, increases gap
                    parentId: FactorName.POSITION_LEVEL,
                    displayValue: 'Senior',
                    representativeShareAllMen: 0.30,
                    representativeShareAllWomen: 0.20,
                    type: FactorTypes.CategoricalChild,
                    decomposedPayGapPercentage: 0.03,
                    malePercentageEffectOnSalary: 0.25,
                    femalePercentageEffectOnSalary: 0.18,
                    coefficient: 0.215,
                    coefficientMale: 0.25,
                    coefficientFemale: 0.18,
                    pValueMen: 0.001,
                    pValueWomen: 0.072,
                    isSignificant: true,
                    isSignificantMen: true,
                    isSignificantWomen: false,
                    excluded: false,
                    excludedReason: null,
                    significanceTrend: SignificanceTrend.MEN,
                } satisfies CategoricalFactorChild,
                {
                    // Neither group significant
                    parentId: FactorName.POSITION_LEVEL,
                    displayValue: 'Manager',
                    representativeShareAllMen: 0.10,
                    representativeShareAllWomen: 0.08,
                    type: FactorTypes.CategoricalChild,
                    decomposedPayGapPercentage: 0.01,
                    malePercentageEffectOnSalary: 0.35,
                    femalePercentageEffectOnSalary: 0.28,
                    coefficient: 0.315,
                    coefficientMale: 0.35,
                    coefficientFemale: 0.28,
                    pValueMen: 0.112,
                    pValueWomen: 0.352,
                    isSignificant: false,
                    isSignificantMen: false,
                    isSignificantWomen: false,
                    excluded: false,
                    excludedReason: null,
                    significanceTrend: SignificanceTrend.NEITHER,
                } satisfies CategoricalFactorChild,
                {
                    // Single-gendered excluded — only men hold Director role
                    parentId: FactorName.POSITION_LEVEL,
                    displayValue: 'Director',
                    representativeShareAllMen: 0.04,
                    representativeShareAllWomen: 0,
                    type: FactorTypes.CategoricalChild,
                    decomposedPayGapPercentage: 0,
                    malePercentageEffectOnSalary: null,
                    femalePercentageEffectOnSalary: null,
                    coefficient: 0,
                    coefficientMale: 0,
                    coefficientFemale: 0,
                    pValueMen: null,
                    pValueWomen: null,
                    isSignificant: false,
                    isSignificantMen: null,
                    isSignificantWomen: null,
                    excluded: true,
                    excludedReason: 'SINGLE_GENDERED',
                    significanceTrend: SignificanceTrend.NEITHER,
                } satisfies CategoricalFactorChild,
            ],
        },
        [FactorName.CONTRACT]: {
            id: FactorName.CONTRACT,
            type: FactorTypes.Categorical,
            decomposedPayGapPercentage: 0.02,
            malePercentageEffectOnSalary: null,
            femalePercentageEffectOnSalary: null,
            coefficient: 0,
            coefficientMale: 0,
            coefficientFemale: 0,
            pValueMen: null,
            pValueWomen: null,
            isSignificant: true,
            isSignificantMen: true,
            isSignificantWomen: true,
            excluded: false,
            excludedReason: null,
            significanceTrend: SignificanceTrend.BOTH,
            canBeImported: true,
            countSingleGendered: 0,
            countAliased: 1,
            children: [
                {
                    parentId: FactorName.CONTRACT,
                    displayValue: '1',
                    representativeShareAllMen: 0.76,
                    representativeShareAllWomen: 0.70,
                    type: FactorTypes.CategoricalChild,
                    decomposedPayGapPercentage: 0,
                    malePercentageEffectOnSalary: null,
                    femalePercentageEffectOnSalary: null,
                    coefficient: 0,
                    coefficientMale: 0,
                    coefficientFemale: 0,
                    pValueMen: null,
                    pValueWomen: null,
                    isSignificant: false,
                    isSignificantMen: null,
                    isSignificantWomen: null,
                    excluded: true,
                    excludedReason: 'REFERENCE_CATEGORY',
                    significanceTrend: SignificanceTrend.NEITHER,
                } satisfies CategoricalFactorChild,
                {
                    // Both groups significant — women over-represented in part-time, reduces gap
                    parentId: FactorName.CONTRACT,
                    displayValue: '2',
                    representativeShareAllMen: 0.12,
                    representativeShareAllWomen: 0.24,
                    type: FactorTypes.CategoricalChild,
                    decomposedPayGapPercentage: -0.015,
                    malePercentageEffectOnSalary: -0.08,
                    femalePercentageEffectOnSalary: -0.06,
                    coefficient: -0.07,
                    coefficientMale: -0.08,
                    coefficientFemale: -0.06,
                    pValueMen: 0.001,
                    pValueWomen: 0.003,
                    isSignificant: true,
                    isSignificantMen: true,
                    isSignificantWomen: true,
                    excluded: false,
                    excludedReason: null,
                    significanceTrend: SignificanceTrend.BOTH,
                } satisfies CategoricalFactorChild,
                {
                    // Aliased excluded term — collinear with another factor
                    parentId: FactorName.CONTRACT,
                    displayValue: '3',
                    representativeShareAllMen: 0.12,
                    representativeShareAllWomen: 0.06,
                    type: FactorTypes.CategoricalChild,
                    decomposedPayGapPercentage: 0,
                    malePercentageEffectOnSalary: null,
                    femalePercentageEffectOnSalary: null,
                    coefficient: 0,
                    coefficientMale: 0,
                    coefficientFemale: 0,
                    pValueMen: null,
                    pValueWomen: null,
                    isSignificant: false,
                    isSignificantMen: null,
                    isSignificantWomen: null,
                    excluded: true,
                    excludedReason: 'ALIASED',
                    significanceTrend: SignificanceTrend.NEITHER,
                } satisfies CategoricalFactorChild,
            ],
        },
        [FactorName.FULLTIME]: {
            // Tests special term translation: 'true' → Full-time, 'false' → Part-time
            id: FactorName.FULLTIME,
            type: FactorTypes.Categorical,
            decomposedPayGapPercentage: 0.015,
            malePercentageEffectOnSalary: null,
            femalePercentageEffectOnSalary: null,
            coefficient: 0,
            coefficientMale: 0,
            coefficientFemale: 0,
            pValueMen: null,
            pValueWomen: null,
            isSignificant: true,
            isSignificantMen: true,
            isSignificantWomen: true,
            excluded: false,
            excludedReason: null,
            significanceTrend: SignificanceTrend.BOTH,
            canBeImported: false,
            countSingleGendered: 0,
            countAliased: 0,
            children: [
                {
                    parentId: FactorName.FULLTIME,
                    displayValue: 'true',
                    representativeShareAllMen: 0.88,
                    representativeShareAllWomen: 0.80,
                    type: FactorTypes.CategoricalChild,
                    decomposedPayGapPercentage: 0,
                    malePercentageEffectOnSalary: null,
                    femalePercentageEffectOnSalary: null,
                    coefficient: 0,
                    coefficientMale: 0,
                    coefficientFemale: 0,
                    pValueMen: null,
                    pValueWomen: null,
                    isSignificant: false,
                    isSignificantMen: null,
                    isSignificantWomen: null,
                    excluded: true,
                    excludedReason: 'REFERENCE_CATEGORY',
                    significanceTrend: SignificanceTrend.NEITHER,
                } satisfies CategoricalFactorChild,
                {
                    // Part-time — women over-represented, both groups significant, reduces gap
                    parentId: FactorName.FULLTIME,
                    displayValue: 'false',
                    representativeShareAllMen: 0.12,
                    representativeShareAllWomen: 0.20,
                    type: FactorTypes.CategoricalChild,
                    decomposedPayGapPercentage: -0.012,
                    malePercentageEffectOnSalary: -0.15,
                    femalePercentageEffectOnSalary: -0.12,
                    coefficient: -0.135,
                    coefficientMale: -0.15,
                    coefficientFemale: -0.12,
                    pValueMen: 0.0002,
                    pValueWomen: 0.001,
                    isSignificant: true,
                    isSignificantMen: true,
                    isSignificantWomen: true,
                    excluded: false,
                    excludedReason: null,
                    significanceTrend: SignificanceTrend.BOTH,
                } satisfies CategoricalFactorChild,
            ],
        },
        [FactorName.MANAGER_CODE]: {
            // Fully excluded at factor level — aliased with org structure
            id: FactorName.MANAGER_CODE,
            type: FactorTypes.Categorical,
            decomposedPayGapPercentage: 0,
            malePercentageEffectOnSalary: null,
            femalePercentageEffectOnSalary: null,
            coefficient: 0,
            coefficientMale: 0,
            coefficientFemale: 0,
            pValueMen: null,
            pValueWomen: null,
            isSignificant: false,
            isSignificantMen: null,
            isSignificantWomen: null,
            excluded: true,
            excludedReason: 'ALIASED',
            significanceTrend: SignificanceTrend.NEITHER,
            canBeImported: true,
            countSingleGendered: 0,
            countAliased: 0,
            children: [],
        },
        [FactorName.LOCALITY]: {
            // City/region of work — women slightly over-represented in lower-paying locations
            id: FactorName.LOCALITY,
            type: FactorTypes.Categorical,
            decomposedPayGapPercentage: 0.02,
            malePercentageEffectOnSalary: null,
            femalePercentageEffectOnSalary: null,
            coefficient: 0,
            coefficientMale: 0,
            coefficientFemale: 0,
            pValueMen: null,
            pValueWomen: null,
            isSignificant: true,
            isSignificantMen: true,
            isSignificantWomen: true,
            excluded: false,
            excludedReason: null,
            significanceTrend: SignificanceTrend.BOTH,
            canBeImported: true,
            countSingleGendered: 0,
            countAliased: 0,
            children: [
                {
                    parentId: FactorName.LOCALITY,
                    displayValue: 'Stockholm',
                    representativeShareAllMen: 0.40,
                    representativeShareAllWomen: 0.44,
                    type: FactorTypes.CategoricalChild,
                    decomposedPayGapPercentage: 0,
                    malePercentageEffectOnSalary: null,
                    femalePercentageEffectOnSalary: null,
                    coefficient: 0,
                    coefficientMale: 0,
                    coefficientFemale: 0,
                    pValueMen: null,
                    pValueWomen: null,
                    isSignificant: false,
                    isSignificantMen: null,
                    isSignificantWomen: null,
                    excluded: true,
                    excludedReason: 'REFERENCE_CATEGORY',
                    significanceTrend: SignificanceTrend.NEITHER,
                } satisfies CategoricalFactorChild,
                {
                    parentId: FactorName.LOCALITY,
                    displayValue: 'Gothenburg',
                    representativeShareAllMen: 0.36,
                    representativeShareAllWomen: 0.28,
                    type: FactorTypes.CategoricalChild,
                    decomposedPayGapPercentage: 0.02,
                    malePercentageEffectOnSalary: 0.06,
                    femalePercentageEffectOnSalary: 0.05,
                    coefficient: 0.055,
                    coefficientMale: 0.06,
                    coefficientFemale: 0.05,
                    pValueMen: 0.003,
                    pValueWomen: 0.006,
                    isSignificant: true,
                    isSignificantMen: true,
                    isSignificantWomen: true,
                    excluded: false,
                    excludedReason: null,
                    significanceTrend: SignificanceTrend.BOTH,
                } satisfies CategoricalFactorChild,
                {
                    parentId: FactorName.LOCALITY,
                    displayValue: 'Malmö',
                    representativeShareAllMen: 0.24,
                    representativeShareAllWomen: 0.28,
                    type: FactorTypes.CategoricalChild,
                    decomposedPayGapPercentage: 0,
                    malePercentageEffectOnSalary: 0.01,
                    femalePercentageEffectOnSalary: 0.008,
                    coefficient: 0.009,
                    coefficientMale: 0.01,
                    coefficientFemale: 0.008,
                    pValueMen: 0.74,
                    pValueWomen: 0.77,
                    isSignificant: false,
                    isSignificantMen: false,
                    isSignificantWomen: false,
                    excluded: false,
                    excludedReason: null,
                    significanceTrend: SignificanceTrend.NEITHER,
                } satisfies CategoricalFactorChild,
            ],
        },
        [FactorName.JOB_FAMILY]: {
            // DS-10 pattern: all 3 term exclusion types in one factor
            id: FactorName.JOB_FAMILY,
            type: FactorTypes.Categorical,
            decomposedPayGapPercentage: 0.04,
            malePercentageEffectOnSalary: null,
            femalePercentageEffectOnSalary: null,
            coefficient: 0,
            coefficientMale: 0,
            coefficientFemale: 0,
            pValueMen: null,
            pValueWomen: null,
            isSignificant: true,
            isSignificantMen: true,
            isSignificantWomen: true,
            excluded: false,
            excludedReason: null,
            significanceTrend: SignificanceTrend.BOTH,
            canBeImported: true,
            countSingleGendered: 1,
            countAliased: 1,
            children: [
                {
                    parentId: FactorName.JOB_FAMILY,
                    displayValue: 'Sales',
                    representativeShareAllMen: 0.28,
                    representativeShareAllWomen: 0.24,
                    type: FactorTypes.CategoricalChild,
                    decomposedPayGapPercentage: 0,
                    malePercentageEffectOnSalary: null,
                    femalePercentageEffectOnSalary: null,
                    coefficient: 0,
                    coefficientMale: 0,
                    coefficientFemale: 0,
                    pValueMen: null,
                    pValueWomen: null,
                    isSignificant: false,
                    isSignificantMen: null,
                    isSignificantWomen: null,
                    excluded: true,
                    excludedReason: 'REFERENCE_CATEGORY',
                    significanceTrend: SignificanceTrend.NEITHER,
                } satisfies CategoricalFactorChild,
                {
                    // Both groups significant — men over-represented in Technology, increases gap
                    parentId: FactorName.JOB_FAMILY,
                    displayValue: 'Technology',
                    representativeShareAllMen: 0.40,
                    representativeShareAllWomen: 0.24,
                    type: FactorTypes.CategoricalChild,
                    decomposedPayGapPercentage: 0.04,
                    malePercentageEffectOnSalary: 0.18,
                    femalePercentageEffectOnSalary: 0.14,
                    coefficient: 0.16,
                    coefficientMale: 0.18,
                    coefficientFemale: 0.14,
                    pValueMen: 0.001,
                    pValueWomen: 0.002,
                    isSignificant: true,
                    isSignificantMen: true,
                    isSignificantWomen: true,
                    excluded: false,
                    excludedReason: null,
                    significanceTrend: SignificanceTrend.BOTH,
                } satisfies CategoricalFactorChild,
                {
                    // Neither group significant
                    parentId: FactorName.JOB_FAMILY,
                    displayValue: 'Marketing',
                    representativeShareAllMen: 0.16,
                    representativeShareAllWomen: 0.20,
                    type: FactorTypes.CategoricalChild,
                    decomposedPayGapPercentage: 0.005,
                    malePercentageEffectOnSalary: 0.05,
                    femalePercentageEffectOnSalary: 0.03,
                    coefficient: 0.04,
                    coefficientMale: 0.05,
                    coefficientFemale: 0.03,
                    pValueMen: 0.532,
                    pValueWomen: 0.668,
                    isSignificant: false,
                    isSignificantMen: false,
                    isSignificantWomen: false,
                    excluded: false,
                    excludedReason: null,
                    significanceTrend: SignificanceTrend.NEITHER,
                } satisfies CategoricalFactorChild,
                {
                    // Single-gendered excluded — only women work in HR
                    parentId: FactorName.JOB_FAMILY,
                    displayValue: 'HR',
                    representativeShareAllMen: 0,
                    representativeShareAllWomen: 0.20,
                    type: FactorTypes.CategoricalChild,
                    decomposedPayGapPercentage: 0,
                    malePercentageEffectOnSalary: null,
                    femalePercentageEffectOnSalary: null,
                    coefficient: 0,
                    coefficientMale: 0,
                    coefficientFemale: 0,
                    pValueMen: null,
                    pValueWomen: null,
                    isSignificant: false,
                    isSignificantMen: null,
                    isSignificantWomen: null,
                    excluded: true,
                    excludedReason: 'SINGLE_GENDERED',
                    significanceTrend: SignificanceTrend.NEITHER,
                } satisfies CategoricalFactorChild,
                {
                    // Aliased excluded — Finance is collinear with another factor
                    parentId: FactorName.JOB_FAMILY,
                    displayValue: 'Finance',
                    representativeShareAllMen: 0.16,
                    representativeShareAllWomen: 0.12,
                    type: FactorTypes.CategoricalChild,
                    decomposedPayGapPercentage: 0,
                    malePercentageEffectOnSalary: null,
                    femalePercentageEffectOnSalary: null,
                    coefficient: 0,
                    coefficientMale: 0,
                    coefficientFemale: 0,
                    pValueMen: null,
                    pValueWomen: null,
                    isSignificant: false,
                    isSignificantMen: null,
                    isSignificantWomen: null,
                    excluded: true,
                    excludedReason: 'ALIASED',
                    significanceTrend: SignificanceTrend.NEITHER,
                } satisfies CategoricalFactorChild,
            ],
        },
        [FactorName.JOB_SUBFAMILY]: {
            // Sub-family within job family — men over-represented in higher-paying sub-families
            id: FactorName.JOB_SUBFAMILY,
            type: FactorTypes.Categorical,
            decomposedPayGapPercentage: 0.03,
            malePercentageEffectOnSalary: null,
            femalePercentageEffectOnSalary: null,
            coefficient: 0,
            coefficientMale: 0,
            coefficientFemale: 0,
            pValueMen: null,
            pValueWomen: null,
            isSignificant: true,
            isSignificantMen: true,
            isSignificantWomen: false,
            excluded: false,
            excludedReason: null,
            significanceTrend: SignificanceTrend.MEN,
            canBeImported: true,
            countSingleGendered: 1,
            countAliased: 0,
            children: [
                {
                    parentId: FactorName.JOB_SUBFAMILY,
                    displayValue: 'Software Engineering',
                    representativeShareAllMen: 0.32,
                    representativeShareAllWomen: 0.20,
                    type: FactorTypes.CategoricalChild,
                    decomposedPayGapPercentage: 0,
                    malePercentageEffectOnSalary: null,
                    femalePercentageEffectOnSalary: null,
                    coefficient: 0,
                    coefficientMale: 0,
                    coefficientFemale: 0,
                    pValueMen: null,
                    pValueWomen: null,
                    isSignificant: false,
                    isSignificantMen: null,
                    isSignificantWomen: null,
                    excluded: true,
                    excludedReason: 'REFERENCE_CATEGORY',
                    significanceTrend: SignificanceTrend.NEITHER,
                } satisfies CategoricalFactorChild,
                {
                    parentId: FactorName.JOB_SUBFAMILY,
                    displayValue: 'Data & Analytics',
                    representativeShareAllMen: 0.28,
                    representativeShareAllWomen: 0.16,
                    type: FactorTypes.CategoricalChild,
                    decomposedPayGapPercentage: 0.03,
                    malePercentageEffectOnSalary: 0.09,
                    femalePercentageEffectOnSalary: 0.06,
                    coefficient: 0.075,
                    coefficientMale: 0.09,
                    coefficientFemale: 0.06,
                    pValueMen: 0.003,
                    pValueWomen: 0.23,
                    isSignificant: true,
                    isSignificantMen: true,
                    isSignificantWomen: false,
                    excluded: false,
                    excludedReason: null,
                    significanceTrend: SignificanceTrend.MEN,
                } satisfies CategoricalFactorChild,
                {
                    // Single-gendered excluded — only women in People & Culture
                    parentId: FactorName.JOB_SUBFAMILY,
                    displayValue: 'People & Culture',
                    representativeShareAllMen: 0,
                    representativeShareAllWomen: 0.24,
                    type: FactorTypes.CategoricalChild,
                    decomposedPayGapPercentage: 0,
                    malePercentageEffectOnSalary: null,
                    femalePercentageEffectOnSalary: null,
                    coefficient: 0,
                    coefficientMale: 0,
                    coefficientFemale: 0,
                    pValueMen: null,
                    pValueWomen: null,
                    isSignificant: false,
                    isSignificantMen: null,
                    isSignificantWomen: null,
                    excluded: true,
                    excludedReason: 'SINGLE_GENDERED',
                    significanceTrend: SignificanceTrend.NEITHER,
                } satisfies CategoricalFactorChild,
            ],
        },
        [FactorName.JOB_CAREER_BAND]: {
            id: FactorName.JOB_CAREER_BAND,
            type: FactorTypes.Categorical,
            decomposedPayGapPercentage: 0.04,
            malePercentageEffectOnSalary: null,
            femalePercentageEffectOnSalary: null,
            coefficient: 0,
            coefficientMale: 0,
            coefficientFemale: 0,
            pValueMen: null,
            pValueWomen: null,
            isSignificant: true,
            isSignificantMen: true,
            isSignificantWomen: true,
            excluded: false,
            excludedReason: null,
            significanceTrend: SignificanceTrend.BOTH,
            canBeImported: true,
            countSingleGendered: 0,
            countAliased: 0,
            children: [
                {
                    parentId: FactorName.JOB_CAREER_BAND,
                    displayValue: 'Band 1',
                    representativeShareAllMen: 0.24,
                    representativeShareAllWomen: 0.36,
                    type: FactorTypes.CategoricalChild,
                    decomposedPayGapPercentage: 0,
                    malePercentageEffectOnSalary: null,
                    femalePercentageEffectOnSalary: null,
                    coefficient: 0,
                    coefficientMale: 0,
                    coefficientFemale: 0,
                    pValueMen: null,
                    pValueWomen: null,
                    isSignificant: false,
                    isSignificantMen: null,
                    isSignificantWomen: null,
                    excluded: true,
                    excludedReason: 'REFERENCE_CATEGORY',
                    significanceTrend: SignificanceTrend.NEITHER,
                } satisfies CategoricalFactorChild,
                {
                    parentId: FactorName.JOB_CAREER_BAND,
                    displayValue: 'Band 2',
                    representativeShareAllMen: 0.40,
                    representativeShareAllWomen: 0.40,
                    type: FactorTypes.CategoricalChild,
                    decomposedPayGapPercentage: 0.01,
                    malePercentageEffectOnSalary: 0.10,
                    femalePercentageEffectOnSalary: 0.09,
                    coefficient: 0.095,
                    coefficientMale: 0.10,
                    coefficientFemale: 0.09,
                    pValueMen: 0.001,
                    pValueWomen: 0.001,
                    isSignificant: true,
                    isSignificantMen: true,
                    isSignificantWomen: true,
                    excluded: false,
                    excludedReason: null,
                    significanceTrend: SignificanceTrend.BOTH,
                } satisfies CategoricalFactorChild,
                {
                    parentId: FactorName.JOB_CAREER_BAND,
                    displayValue: 'Band 3',
                    representativeShareAllMen: 0.36,
                    representativeShareAllWomen: 0.24,
                    type: FactorTypes.CategoricalChild,
                    decomposedPayGapPercentage: 0.03,
                    malePercentageEffectOnSalary: 0.22,
                    femalePercentageEffectOnSalary: 0.15,
                    coefficient: 0.185,
                    coefficientMale: 0.22,
                    coefficientFemale: 0.15,
                    pValueMen: 0.001,
                    pValueWomen: 0.096,
                    isSignificant: true,
                    isSignificantMen: true,
                    isSignificantWomen: false,
                    excluded: false,
                    excludedReason: null,
                    significanceTrend: SignificanceTrend.MEN,
                } satisfies CategoricalFactorChild,
            ],
        },
        [FactorName.ADDITIONALDATAONE]: {
            id: FactorName.ADDITIONALDATAONE,
            type: FactorTypes.Categorical,
            decomposedPayGapPercentage: 0.01,
            malePercentageEffectOnSalary: null,
            femalePercentageEffectOnSalary: null,
            coefficient: 0,
            coefficientMale: 0,
            coefficientFemale: 0,
            pValueMen: null,
            pValueWomen: null,
            isSignificant: false,
            isSignificantMen: false,
            isSignificantWomen: false,
            excluded: false,
            excludedReason: null,
            significanceTrend: SignificanceTrend.NEITHER,
            canBeImported: true,
            countSingleGendered: 0,
            countAliased: 0,
            children: [
                {
                    parentId: FactorName.ADDITIONALDATAONE,
                    displayValue: 'A',
                    representativeShareAllMen: 0.56,
                    representativeShareAllWomen: 0.50,
                    type: FactorTypes.CategoricalChild,
                    decomposedPayGapPercentage: 0,
                    malePercentageEffectOnSalary: null,
                    femalePercentageEffectOnSalary: null,
                    coefficient: 0,
                    coefficientMale: 0,
                    coefficientFemale: 0,
                    pValueMen: null,
                    pValueWomen: null,
                    isSignificant: false,
                    isSignificantMen: null,
                    isSignificantWomen: null,
                    excluded: true,
                    excludedReason: 'REFERENCE_CATEGORY',
                    significanceTrend: SignificanceTrend.NEITHER,
                } satisfies CategoricalFactorChild,
                {
                    parentId: FactorName.ADDITIONALDATAONE,
                    displayValue: 'B',
                    representativeShareAllMen: 0.44,
                    representativeShareAllWomen: 0.50,
                    type: FactorTypes.CategoricalChild,
                    decomposedPayGapPercentage: 0.01,
                    malePercentageEffectOnSalary: 0.02,
                    femalePercentageEffectOnSalary: 0.015,
                    coefficient: 0.018,
                    coefficientMale: 0.02,
                    coefficientFemale: 0.015,
                    pValueMen: 0.62,
                    pValueWomen: 0.69,
                    isSignificant: false,
                    isSignificantMen: false,
                    isSignificantWomen: false,
                    excluded: false,
                    excludedReason: null,
                    significanceTrend: SignificanceTrend.NEITHER,
                } satisfies CategoricalFactorChild,
            ],
        },
        [FactorName.ADDITIONALDATATWO]: {
            id: FactorName.ADDITIONALDATATWO,
            type: FactorTypes.Categorical,
            decomposedPayGapPercentage: 0.01,
            malePercentageEffectOnSalary: null,
            femalePercentageEffectOnSalary: null,
            coefficient: 0,
            coefficientMale: 0,
            coefficientFemale: 0,
            pValueMen: null,
            pValueWomen: null,
            isSignificant: false,
            isSignificantMen: false,
            isSignificantWomen: false,
            excluded: false,
            excludedReason: null,
            significanceTrend: SignificanceTrend.NEITHER,
            canBeImported: true,
            countSingleGendered: 0,
            countAliased: 0,
            children: [
                {
                    parentId: FactorName.ADDITIONALDATATWO,
                    displayValue: 'X',
                    representativeShareAllMen: 0.60,
                    representativeShareAllWomen: 0.56,
                    type: FactorTypes.CategoricalChild,
                    decomposedPayGapPercentage: 0,
                    malePercentageEffectOnSalary: null,
                    femalePercentageEffectOnSalary: null,
                    coefficient: 0,
                    coefficientMale: 0,
                    coefficientFemale: 0,
                    pValueMen: null,
                    pValueWomen: null,
                    isSignificant: false,
                    isSignificantMen: null,
                    isSignificantWomen: null,
                    excluded: true,
                    excludedReason: 'REFERENCE_CATEGORY',
                    significanceTrend: SignificanceTrend.NEITHER,
                } satisfies CategoricalFactorChild,
                {
                    parentId: FactorName.ADDITIONALDATATWO,
                    displayValue: 'Y',
                    representativeShareAllMen: 0.40,
                    representativeShareAllWomen: 0.44,
                    type: FactorTypes.CategoricalChild,
                    decomposedPayGapPercentage: 0.01,
                    malePercentageEffectOnSalary: 0.015,
                    femalePercentageEffectOnSalary: 0.012,
                    coefficient: 0.014,
                    coefficientMale: 0.015,
                    coefficientFemale: 0.012,
                    pValueMen: 0.67,
                    pValueWomen: 0.71,
                    isSignificant: false,
                    isSignificantMen: false,
                    isSignificantWomen: false,
                    excluded: false,
                    excludedReason: null,
                    significanceTrend: SignificanceTrend.NEITHER,
                } satisfies CategoricalFactorChild,
            ],
        },
        [FactorName.ADDITIONALDATATHREE]: {
            id: FactorName.ADDITIONALDATATHREE,
            type: FactorTypes.Categorical,
            decomposedPayGapPercentage: 0.01,
            malePercentageEffectOnSalary: null,
            femalePercentageEffectOnSalary: null,
            coefficient: 0,
            coefficientMale: 0,
            coefficientFemale: 0,
            pValueMen: null,
            pValueWomen: null,
            isSignificant: false,
            isSignificantMen: false,
            isSignificantWomen: false,
            excluded: false,
            excludedReason: null,
            significanceTrend: SignificanceTrend.NEITHER,
            canBeImported: true,
            countSingleGendered: 0,
            countAliased: 0,
            children: [
                {
                    parentId: FactorName.ADDITIONALDATATHREE,
                    displayValue: 'P',
                    representativeShareAllMen: 0.64,
                    representativeShareAllWomen: 0.60,
                    type: FactorTypes.CategoricalChild,
                    decomposedPayGapPercentage: 0,
                    malePercentageEffectOnSalary: null,
                    femalePercentageEffectOnSalary: null,
                    coefficient: 0,
                    coefficientMale: 0,
                    coefficientFemale: 0,
                    pValueMen: null,
                    pValueWomen: null,
                    isSignificant: false,
                    isSignificantMen: null,
                    isSignificantWomen: null,
                    excluded: true,
                    excludedReason: 'REFERENCE_CATEGORY',
                    significanceTrend: SignificanceTrend.NEITHER,
                } satisfies CategoricalFactorChild,
                {
                    parentId: FactorName.ADDITIONALDATATHREE,
                    displayValue: 'Q',
                    representativeShareAllMen: 0.36,
                    representativeShareAllWomen: 0.40,
                    type: FactorTypes.CategoricalChild,
                    decomposedPayGapPercentage: 0.01,
                    malePercentageEffectOnSalary: 0.018,
                    femalePercentageEffectOnSalary: 0.014,
                    coefficient: 0.016,
                    coefficientMale: 0.018,
                    coefficientFemale: 0.014,
                    pValueMen: 0.65,
                    pValueWomen: 0.70,
                    isSignificant: false,
                    isSignificantMen: false,
                    isSignificantWomen: false,
                    excluded: false,
                    excludedReason: null,
                    significanceTrend: SignificanceTrend.NEITHER,
                } satisfies CategoricalFactorChild,
            ],
        },
    };

    // ─── Default Fallbacks ────────────────────────────────────────────────────

    function toFactorName(name: string): FactorName | undefined {
        return Object.values(FactorName).find(fn => fn === name);
    }

    function defaultNumericalFactor(name: FactorName): NumericalFactor {
        return {
            id: name,
            type: FactorTypes.Linear,
            decomposedPayGapPercentage: 0,
            malePercentageEffectOnSalary: null,
            femalePercentageEffectOnSalary: null,
            coefficient: 0,
            coefficientMale: 0,
            coefficientFemale: 0,
            pValueMen: null,
            pValueWomen: null,
            isSignificant: false,
            isSignificantMen: null,
            isSignificantWomen: null,
            excluded: false,
            excludedReason: null,
            significanceTrend: SignificanceTrend.NEITHER,
            meanValueMen: 0,
            meanValueWomen: 0,
            canBeImported: true,
        };
    }

    function defaultCategoricalFactor(name: FactorName): CategoricalFactor {
        return {
            id: name,
            type: FactorTypes.Categorical,
            decomposedPayGapPercentage: 0,
            malePercentageEffectOnSalary: null,
            femalePercentageEffectOnSalary: null,
            coefficient: 0,
            coefficientMale: 0,
            coefficientFemale: 0,
            pValueMen: null,
            pValueWomen: null,
            isSignificant: false,
            isSignificantMen: null,
            isSignificantWomen: null,
            excluded: false,
            excludedReason: null,
            significanceTrend: SignificanceTrend.NEITHER,
            children: [],
            canBeImported: true,
            countSingleGendered: 0,
            countAliased: 0,
        };
    }

    // ─── Factor Collection Builder ────────────────────────────────────────────
    //
    // Returns a flat array matching what the backend sends:
    // - NumericalFactor with type === Linear
    // - CategoricalFactor with type === Categorical and children: []
    // - CategoricalFactorChild with type === CategoricalChild (one item per child, flat)

    type FlatFactor = NumericalFactor | CategoricalFactor | CategoricalFactorChild;

    function collectFactors(factors: AdjustedSalaryFactor[]): FlatFactor[] {
        const result: FlatFactor[] = [];
        factors.forEach(factor => {
            const name = toFactorName(factor.name);
            if (name === undefined) return;
            if (factor.isCategoricalFactor) {
                const categorical = categoricalFactorRegistry[name] ?? defaultCategoricalFactor(name);
                result.push({ ...categorical });
                categorical.children.forEach(child => result.push({ ...child, type: FactorTypes.CategoricalChild }));
            } else {
                result.push(numericalFactorRegistry[name] ?? defaultNumericalFactor(name));
            }
        });
        return result;
    }

    // ─── Impact Helpers ───────────────────────────────────────────────────────
    // Only considers top-level factors (those with `id`) — not CategoricalFactorChild items.
    // Uses the `type` discriminant to narrow FlatFactor to NumericalFactor | CategoricalFactor.

    function findBiggestImpact(factors: FlatFactor[]): FactorName | null {
        let biggest: NumericalFactor | CategoricalFactor | null = null;
        let biggestAbsGap = -Infinity;
        for (const f of factors) {
            if (f.type === FactorTypes.CategoricalChild) continue;
            if (f.excluded === true || f.decomposedPayGapPercentage === null) continue;
            const absGap = Math.abs(f.decomposedPayGapPercentage);
            if (absGap > biggestAbsGap) {
                biggest = f;
                biggestAbsGap = absGap;
            }
        }
        return biggest?.id ?? null;
    }

    function findSmallestImpact(factors: FlatFactor[]): FactorName | null {
        let smallest: NumericalFactor | CategoricalFactor | null = null;
        let maxAbsGap = -Infinity;
        let minAbsGap = Infinity;
        let activeCount = 0;
        for (const f of factors) {
            if (f.type === FactorTypes.CategoricalChild) continue;
            if (f.excluded === true || f.decomposedPayGapPercentage === null || f.decomposedPayGapPercentage === 0) continue;
            const absGap = Math.abs(f.decomposedPayGapPercentage);
            activeCount++;
            if (absGap > maxAbsGap) maxAbsGap = absGap;
            if (absGap < minAbsGap) {
                minAbsGap = absGap;
                smallest = f;
            }
        }
        if (activeCount <= 1 || maxAbsGap === minAbsGap) return null;
        return smallest?.id ?? null;
    }

    // ─── Base Analysis Builder ────────────────────────────────────────────────
    //
    // Default values based on DS-01 test dataset:
    //   100 employees (50M/50F), Swedish SEK annual salaries
    //   Male median ~520,000 SEK/yr, Female median ~426,000 SEK/yr
    //   Unadjusted gap: 18.1% | Adjusted gap: 2.0%
    //   maleModelStrength: 0.82, femaleModelStrength: 0.78 → modelStrengthLevel: 'strong' (combinedModelStrength: 0.78)
    //
    // All "percentage" fields are decimal: 0.181 = 18.1%, 0.02 = 2%, etc.
    // combinedModelStrength is also decimal (0–1). The UI multiplies by 100 for CSS bar positioning.

    type ExtendedSummary = PayEquityAuditMathsSummary & { explainedPayGap: number };

    function buildBaseAnalysis(
        inputFactors: AdjustedSalaryFactor[],
        overrides: Partial<ExtendedSummary> = {}
    ): ExtendedSummary {
        const factors = collectFactors(inputFactors);
        const base: ExtendedSummary = {
            success: true,
            errorMessage: '',
            errorCode: MathsServiceError.OLS_FAILED,
            maleModelStrength: 0.82,
            femaleModelStrength: 0.78,
            combinedModelStrength: 0.78,
            modelStrengthLevel: 'strong',
            unadjustedPayGap: 0.181,
            adjustedPayGap: 0.02,
            explainedPayGap: 0,
            biggestImpact: findBiggestImpact(factors),
            smallestImpact: findSmallestImpact(factors),
            factors,
            ...overrides,
        };
        // Compute explainedPayGap from the final unadjusted/adjusted values unless explicitly overridden
        base.explainedPayGap = overrides.explainedPayGap ?? base.unadjustedPayGap - base.adjustedPayGap;
        return base;
    }

    // ─── Error Result Builder ─────────────────────────────────────────────────
    //
    // Returns success: false the same way the real endpoint would.
    // The queryFn checks success === false, extracts errorCode, and throws.

    function buildErrorResult(errorCode: MathsServiceError): ExtendedSummary {
        return buildBaseAnalysis([], {
            success: false,
            errorCode,
            errorMessage: errorCode,
        });
    }

    // ─── Main Analysis Generator ──────────────────────────────────────────────

    function generateMockSalaryFactorAnalysis(factors: AdjustedSalaryFactor[]): ExtendedSummary {
        const scenario = currentMockScenario.value;

        // Hard error scenarios — return success: false so the queryFn throws
        if (scenario === 'error-e001') return buildErrorResult(MathsServiceError.ALIASED_TERMS);
        if (scenario === 'error-e002') return buildErrorResult(MathsServiceError.NOT_SIGNIFICANT);
        if (scenario === 'error-e003') return buildErrorResult(MathsServiceError.ALREADY_IN_RANGE);
        if (scenario === 'error-e004') return buildErrorResult(MathsServiceError.NO_ID_BODY);
        if (scenario === 'error-e005') return buildErrorResult(MathsServiceError.NO_ID_EMPS);
        if (scenario === 'error-e006') return buildErrorResult(MathsServiceError.NO_STRATEGY);
        if (scenario === 'error-e007') return buildErrorResult(MathsServiceError.MODEL_FAILED);
        if (scenario === 'error-e008') return buildErrorResult(MathsServiceError.PERFECT_FIT);
        if (scenario === 'error-e009') return buildErrorResult(MathsServiceError.R_SQUARED_MISSING);
        if (scenario === 'error-e010') return buildErrorResult(MathsServiceError.ONE_GROUP);
        if (scenario === 'error-e011') return buildErrorResult(MathsServiceError.CONSTANT_PREDICTORS);
        if (scenario === 'error-e012') return buildErrorResult(MathsServiceError.DATA_TYPE_ERROR);
        if (scenario === 'error-e013') return buildErrorResult(MathsServiceError.OLS_FAILED);
        if (scenario === 'error-default') return buildErrorResult('UNKNOWN_ERROR' as MathsServiceError);

        // Soft error: E001S — AGE aliases with EMPLOYMENT_TIME (r≈0.999)
        // AGE is excluded from the model; EMPLOYMENT_TIME carries the tenure signal.
        if (scenario === 'soft-error-e001s') {
            const base = buildBaseAnalysis(factors, {
                errorCode: MathsServiceError.ALIASED_TERMS_SOFT_ERROR,
                unadjustedPayGap: 0.08,
                adjustedPayGap: 0.02,
                maleModelStrength: 0.76,
                femaleModelStrength: 0.72,
                combinedModelStrength: 0.72,
            });
            return {
                ...base,
                factors: base.factors.map(f =>
                    'id' in f && f.id === FactorName.AGE
                        ? { ...f, excluded: true, excludedReason: 'ALIASED' as const }
                        : f
                ),
            };
        }

        // Soft error: E014 — pay gap ~300 SEK (~0.3%) — too small for Blinder-Oaxaca decomposition.
        // System falls back to a direct isFemale regression. Per-factor decomposition is unavailable:
        // - decomposedPayGapPercentage is 0 (no Oaxaca decomposition was run)
        // - pValueMen/pValueWomen are null (significance not calculated per-factor in direct method)
        // - isSignificant/isSignificantMen/isSignificantWomen are null for the same reason
        if (scenario === 'soft-error-e014') {
            const base = buildBaseAnalysis(factors, {
                errorCode: MathsServiceError.RELATIVE_DIFFERENCE_IN_GEOMETRIC_MEANS,
                unadjustedPayGap: 0.003,
                adjustedPayGap: 0.001,
                maleModelStrength: 0.74,
                femaleModelStrength: 0.71,
                combinedModelStrength: 0.71,
                biggestImpact: null,
                smallestImpact: null,
            });
            return {
                ...base,
                factors: base.factors.map(f => ({
                    ...f,
                    decomposedPayGapPercentage: 0,
                    pValueMen: null,
                    pValueWomen: null,
                    isSignificant: false,
                    isSignificantMen: null,
                    isSignificantWomen: null,
                })),
            };
        }

        // Data quality: AGE factor excluded due to insufficient data (missing birthdates)
        if (scenario === 'needs-more-data') {
            const base = buildBaseAnalysis(factors);
            return {
                ...base,
                factors: base.factors.map(f =>
                    'id' in f && f.id === FactorName.AGE
                        ? { ...f, excluded: true, excludedReason: 'INSUFFICIENT_VALUES' as const }
                        : f
                ),
            };
        }

        // Model strength: Weak (combinedModelStrength < 0.30)
        if (scenario === 'weak-model') {
            return buildBaseAnalysis(factors, {
                maleModelStrength: 0.15,
                femaleModelStrength: 0.12,
                combinedModelStrength: 0.12,
                modelStrengthLevel: 'weak',
            });
        }

        // Model strength: Moderate (0.30 ≤ combinedModelStrength < 0.50)
        if (scenario === 'moderate-model') {
            return buildBaseAnalysis(factors, {
                maleModelStrength: 0.40,
                femaleModelStrength: 0.38,
                combinedModelStrength: 0.38,
                modelStrengthLevel: 'moderate',
            });
        }

        // Model strength: Strong (combinedModelStrength ≥ 0.70) — explicitly higher than default base
        if (scenario === 'strong-model') {
            return buildBaseAnalysis(factors, {
                maleModelStrength: 0.85,
                femaleModelStrength: 0.82,
                combinedModelStrength: 0.85,
                modelStrengthLevel: 'strong',
            });
        }

        // Model strength: Asymmetric — men well-explained, women poorly explained
        if (scenario === 'asymmetric-r2') {
            return buildBaseAnalysis(factors, {
                maleModelStrength: 0.72,
                femaleModelStrength: 0.22,
                // combinedModelStrength reflects the lower of the two
                combinedModelStrength: 0.22,
                modelStrengthLevel: 'weak',
            });
        }

        // Pay gap direction: Women favoured
        if (scenario === 'women-favoured') {
            return buildBaseAnalysis(factors, {
                unadjustedPayGap: -0.05,
                adjustedPayGap: -0.03,
            });
        }

        // Pay gap direction: No significant gap after adjustment
        if (scenario === 'no-significant-gap') {
            return buildBaseAnalysis(factors, {
                unadjustedPayGap: 0.03,
                adjustedPayGap: 0.001,
            });
        }

        // Pay gap direction: Crossover — unadjusted favours men, adjusted favours women
        if (scenario === 'crossover') {
            return buildBaseAnalysis(factors, {
                unadjustedPayGap: 0.06,
                adjustedPayGap: -0.02,
            });
        }

        // Default: men-favoured-good-model
        return buildBaseAnalysis(factors);
    }

    // ─── Salary Factor CRUD ───────────────────────────────────────────────────

    function addMockSalaryFactor(input: CreateAdjustedSalaryFactorInput): void {
        const newFactor: AdjustedSalaryFactor = {
            id: `factor-${input.name}`,
            name: input.name,
            isCategoricalFactor: input.isCategoricalFactor,
        };
        salaryFactors.value.list.push(mockModel(newFactor));
    }

    function removeMockSalaryFactor(id: string): void {
        const index = salaryFactors.value.list.findIndex((f) => f.id === id);
        if (index !== -1) {
            salaryFactors.value.list.splice(index, 1);
        }
    }

    return {
        salaryFactors,
        addMockSalaryFactor,
        removeMockSalaryFactor,
        generateMockSalaryFactorAnalysis,
    };
}
